import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { EditableField, ReplaceButton } from "@/site/Editable";
import { errMsg, imgSrc, useSite } from "@/site/SiteContext";

const AdminTools = ({ project, index, setIndex }) => {
  const { saveProject } = useSite();
  const imgs = project.images;
  const run = async (patch, next) => {
    try {
      await saveProject(project.id, patch);
      if (next !== undefined) setIndex(next);
      toast.success("Showroom updated");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const move = (dir) => {
    const j = index + dir;
    const list = [...imgs];
    [list[index], list[j]] = [list[j], list[index]];
    run({ images: list }, j);
  };
  const remove = () => {
    const list = imgs.filter((_, i) => i !== index);
    run({ images: list, ...(project.cover === imgs[index] ? { cover: list[0] } : {}) }, Math.max(0, index - 1));
  };
  return (
    <div className="hs-show__tools" data-testid="showroom-admin-tools">
      <ReplaceButton label="Add image" onReplace={(url) => run({ images: [...imgs, url] }, imgs.length)} testId="showroom-add-image" />
      <ReplaceButton label="Replace this image" onReplace={(url) => run({ images: imgs.map((u, i) => (i === index ? url : u)), ...(project.cover === imgs[index] ? { cover: url } : {}) })} testId="showroom-replace-image" />
      <button type="button" disabled={index === 0} onClick={() => move(-1)} data-testid="showroom-move-left"><ArrowLeft size={14} /> Move</button>
      <button type="button" disabled={index === imgs.length - 1} onClick={() => move(1)} data-testid="showroom-move-right">Move <ArrowRight size={14} /></button>
      <button type="button" disabled={project.cover === imgs[index]} onClick={() => run({ cover: imgs[index] })} data-testid="showroom-set-cover"><Star size={14} /> Set as cover</button>
      <button type="button" disabled={imgs.length < 2} onClick={remove} data-testid="showroom-remove-image"><Trash2 size={14} /> Remove</button>
    </div>
  );
};

export const Showroom = ({ project, onClose }) => {
  const { editing, saveProject } = useSite();
  const [index, setIndex] = useState(0);
  const touch = useRef(null);
  const n = project.images.length;
  const i = Math.min(index, n - 1);
  const go = useCallback((d) => setIndex((x) => (Math.min(x, n - 1) + d + n) % n), [n]);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [go, onClose]);

  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - (touch.current ?? 0);
    if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
  };

  return createPortal(
    <div className="hs-show" role="dialog" aria-modal="true" aria-label={project.title} data-testid="showroom">
      <div className="hs-show__head">
        <div>
          <EditableField value={project.title} onSave={(title) => saveProject(project.id, { title })} as="h2" className="hs-show__title" multiline={false} testId="showroom-title" />
          <span className="hs-caption">{project.location} · {i + 1} / {n}</span>
        </div>
        <button className="hs-show__close" onClick={onClose} aria-label="Close showroom" data-testid="showroom-close-button"><X size={26} strokeWidth={1.25} /></button>
      </div>
      <div className="hs-show__stage" onTouchStart={(e) => { touch.current = e.touches[0].clientX; }} onTouchEnd={onTouchEnd}>
        <button className="hs-show__arrow is-prev" onClick={() => go(-1)} aria-label="Previous image" data-testid="showroom-prev-button"><ChevronLeft size={34} strokeWidth={1} /></button>
        <img key={project.images[i]} src={imgSrc(project.images[i])} alt={`${project.title} ${i + 1}`} className="hs-show__img" style={{ opacity: 0 }} onLoad={(e) => { e.currentTarget.style.opacity = 1; }} data-testid="showroom-main-image" />
        <button className="hs-show__arrow is-next" onClick={() => go(1)} aria-label="Next image" data-testid="showroom-next-button"><ChevronRight size={34} strokeWidth={1} /></button>
      </div>
      {editing && <AdminTools project={project} index={i} setIndex={setIndex} />}
      <div className="hs-show__thumbs" data-testid="showroom-thumbnails">
        {project.images.map((u, k) => (
          <button key={`${u}-${k}`} className={k === i ? "is-active" : ""} onClick={() => setIndex(k)} aria-label={`Show image ${k + 1}`} data-testid={`showroom-thumb-${k}`}>
            <img src={imgSrc(u)} alt="" loading="lazy" />
          </button>
        ))}
      </div>
    </div>,
    document.querySelector(".hs") || document.body,
  );
};
